import Button from 'devextreme-react/button';
import { Item } from 'devextreme-react/data-grid';
import FileUploader from 'devextreme-react/file-uploader';
import Popup from 'devextreme-react/popup';
import { useEffect, useState, type ReactNode } from 'react';
import type { OpsGridToolbarMenuItem } from '../opsDataGrid/OpsGridToolbar';
import { showErrorToast, showSuccessToast } from '../notifications/toast';
import './dashboardTipsImport.css';

export interface DashboardTipImportRowError {
  index: number;
  title?: string | null;
  message: string;
}

export interface DashboardTipImportResult {
  inserted: number;
  updated: number;
  skipped: number;
  errors: DashboardTipImportRowError[];
}

export interface DashboardTipsJsonImportApi {
  downloadImportTemplate: () => Promise<void>;
  importJson: (file: File) => Promise<DashboardTipImportResult>;
}

export interface UseDashboardTipsJsonImportOptions {
  api: DashboardTipsJsonImportApi;
  disabled?: boolean;
  onImported?: () => void;
}

export interface DashboardTipsJsonImportUi {
  uploadToolbarItem: ReactNode;
  uploadMenuItem: OpsGridToolbarMenuItem;
  importPopup: ReactNode;
}

export function useDashboardTipsJsonImport({
  api,
  disabled,
  onImported
}: UseDashboardTipsJsonImportOptions): DashboardTipsJsonImportUi {
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(false);

  const openImport = () => setOpen(true);

  const uploadToolbarItem = (
    <Item
      location="after"
      widget="dxButton"
      options={{
        icon: 'upload',
        hint: 'Import tips JSON',
        stylingMode: 'outlined',
        disabled: disabled || importing,
        onClick: openImport
      }}
    />
  );

  const uploadMenuItem: OpsGridToolbarMenuItem = {
    id: 'import-tips-json',
    text: 'Import tips JSON',
    icon: 'upload',
    disabled: disabled || importing,
    onClick: openImport
  };

  const importPopup = (
    <DashboardTipsJsonImportPopup
      api={api}
      visible={open}
      importing={importing}
      setImporting={setImporting}
      onClose={() => {
        if (!importing) setOpen(false);
      }}
      onImported={() => onImported?.()}
    />
  );

  return { uploadToolbarItem, uploadMenuItem, importPopup };
}

interface DashboardTipsJsonImportPopupProps {
  api: DashboardTipsJsonImportApi;
  visible: boolean;
  importing: boolean;
  setImporting: (value: boolean) => void;
  onClose: () => void;
  onImported: () => void;
}

function DashboardTipsJsonImportPopup({
  api,
  visible,
  importing,
  setImporting,
  onClose,
  onImported
}: DashboardTipsJsonImportPopupProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [result, setResult] = useState<DashboardTipImportResult | null>(null);
  const [uploaderKey, setUploaderKey] = useState(0);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const selectedFileName = files[0]?.name ?? null;

  useEffect(() => {
    if (!visible) return;
    setFiles([]);
    setResult(null);
    setUploaderKey((value) => value + 1);
  }, [visible]);

  async function downloadTemplate() {
    setDownloadingTemplate(true);
    try {
      await api.downloadImportTemplate();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Failed to download template.');
    } finally {
      setDownloadingTemplate(false);
    }
  }

  async function runImport() {
    if (files.length === 0) {
      showErrorToast('Choose a JSON file to import.');
      return;
    }

    setImporting(true);
    setResult(null);
    try {
      const nextResult = await api.importJson(files[0]);
      setResult(nextResult);
      setFiles([]);
      setUploaderKey((value) => value + 1);
      onImported();

      if (nextResult.inserted > 0 || nextResult.updated > 0) {
        showSuccessToast(
          `Imported ${nextResult.inserted} new · updated ${nextResult.updated} tip(s).`
        );
      } else if (nextResult.errors.length > 0) {
        showErrorToast('No tips imported. Check the row errors below.');
      } else {
        showErrorToast('No tips found in the file.');
      }
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'JSON import failed.');
    } finally {
      setImporting(false);
    }
  }

  return (
    <Popup
      visible={visible}
      title="Import Tips & Tricks"
      showTitle
      showCloseButton
      dragEnabled
      hideOnOutsideClick={!importing}
      width={520}
      maxHeight="90vh"
      height="auto"
      onHiding={onClose}
    >
      <div className="tips-json-import">
        <div className="tips-json-import__callout" role="note">
          <span className="tips-json-import__callout-icon dx-icon-info" aria-hidden />
          <p>
            Import upserts by title (case-insensitive). Matching titles update; new titles are
            created. Use the sample template for the full field set.
          </p>
        </div>

        <section className="tips-json-import__panel">
          <div className="tips-json-import__panel-head">
            <span className="tips-json-import__step-badge">1</span>
            <div>
              <div className="tips-json-import__panel-title">Download template</div>
              <p className="tips-json-import__panel-desc">
                Four example tips with title, body, category, minRole, sortOrder, and isActive.
              </p>
            </div>
          </div>
          <Button
            text={downloadingTemplate ? 'Downloading…' : 'Download JSON template'}
            icon="download"
            stylingMode="outlined"
            width="100%"
            disabled={importing || downloadingTemplate}
            onClick={() => void downloadTemplate()}
          />
        </section>

        <section className="tips-json-import__panel">
          <div className="tips-json-import__panel-head">
            <span className="tips-json-import__step-badge">2</span>
            <div>
              <div className="tips-json-import__panel-title">Upload completed file</div>
              <p className="tips-json-import__panel-desc">
                Choose a JSON file or drag it into the area below.
              </p>
            </div>
          </div>
          <div className="tips-json-import__dropzone">
            <FileUploader
              key={uploaderKey}
              accept=".json,application/json"
              multiple={false}
              uploadMode="useForm"
              selectButtonText="Choose file"
              labelText=""
              onValueChanged={(e) => setFiles((e.value as File[]) ?? [])}
            />
            <p className="tips-json-import__dropzone-hint">JSON files only · one file at a time</p>
            {selectedFileName ? (
              <p className="tips-json-import__selected-file">
                <span className="dx-icon-doc" aria-hidden /> {selectedFileName}
              </p>
            ) : null}
          </div>
        </section>

        {result ? (
          <section
            className={`tips-json-import__results${result.errors.length > 0 ? ' tips-json-import__results--errors' : ''}`}
            aria-live="polite"
          >
            <div className="tips-json-import__results-summary">
              <strong>Import finished</strong>
              <span>
                {result.inserted} inserted · {result.updated} updated · {result.skipped} skipped ·{' '}
                {result.errors.length} error{result.errors.length === 1 ? '' : 's'}
              </span>
            </div>
            {result.errors.length > 0 ? (
              <div className="tips-json-import__errors">
                {result.errors.slice(0, 50).map((err, index) => (
                  <div key={`${err.index}-${err.title ?? ''}-${index}`} className="tips-json-import__error-row">
                    <span className="tips-json-import__error-meta">
                      Item {err.index + 1}
                      {err.title ? ` · ${err.title}` : ''}
                    </span>
                    <span>{err.message}</span>
                  </div>
                ))}
                {result.errors.length > 50 ? (
                  <p className="tips-json-import__errors-more">
                    Showing first 50 errors. Fix those and retry to see the next set.
                  </p>
                ) : null}
              </div>
            ) : null}
          </section>
        ) : null}

        <footer className="tips-json-import__footer">
          <Button text="Cancel" stylingMode="text" disabled={importing} onClick={onClose} />
          <Button
            text={importing ? 'Importing…' : 'Import JSON'}
            type="default"
            icon="upload"
            disabled={importing || files.length === 0}
            onClick={() => void runImport()}
          />
        </footer>
      </div>
    </Popup>
  );
}
